/**
 * Low-level authenticated fetch helpers used by every staff-facing
 * screen in the web ops console.  Reads the current token straight
 * from the session store, so callers don't have to thread the token
 * through every component.
 *
 * A 401 response clears the session globally; any open page that
 * depends on ``useStaffSession`` will flip back to ``unauthenticated``
 * and the app will redirect to /staff/login.
 */

import { API_BASES } from "../../config/apiConfig";
import { getStaffToken, setStaffToken, setStaffPayload } from "../session/staffSession";
import { formatApiError } from "../../util/formatApiError";
import { showToast } from "../../util/appToast";

function handleUnauthorized() {
  setStaffToken(null);
  setStaffPayload(null);
}

async function parseResponse(response) {
  var raw;
  try { raw = await response.json(); } catch (_) { raw = null; }
  if (response.status === 401) {
    handleUnauthorized();
    var expired = formatApiError((raw && raw.message) || raw || "Session expired, please log in again.");
    var err = new Error(expired);
    err.status = 401;
    showToast(expired, "error");
    throw err;
  }
  if (!response.ok || (raw && raw.success === false)) {
    var msg = formatApiError((raw && raw.message) || raw || "Request failed");
    var e = new Error(msg);
    e.status = response.status;
    e.payload = raw && raw.payload;
    if (response.status !== 401) showToast(msg, "error");
    throw e;
  }
  return raw && raw.payload;
}

function buildQuery(params) {
  if (!params) return "";
  var parts = [];
  Object.keys(params).forEach(function (k) {
    var v = params[k];
    if (v === undefined || v === null || v === "") return;
    parts.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
  });
  return parts.length ? "?" + parts.join("&") : "";
}

function authedFetch(base, path, options) {
  options = options || {};
  var token = getStaffToken();
  var headers = Object.assign(
    { "Content-Type": "application/json" },
    options.headers || {}
  );
  if (token) headers.Authorization = "Bearer " + token;
  return fetch(base + path, Object.assign({}, options, { headers: headers }))
    .then(parseResponse);
}

export function staffGet(base, path, params) {
  return authedFetch(base, path + buildQuery(params), { method: "GET" });
}

export function staffPost(base, path, body) {
  return authedFetch(base, path, {
    method: "POST",
    body: JSON.stringify(body || {})
  });
}

// ── Bases (from apiConfig) ───────────────────────────────────────
export var BASES = {
  EVENTS: API_BASES.EVENTS,
  VALET: API_BASES.VALET,
  CART: API_BASES.CART,
  VENDORPAY: API_BASES.VENDORPAY
};
